import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import Modal from '~/app/common/Modal';
import useTranslation from '~/app/hooks/useTranslation';
import Field from '~/app/modules/form/Field';
import Text from '~/app/modules/form/Text';
import Textarea from '~/app/modules/form/Textarea';
import ToggleSwitch from '~/app/modules/form/ToggleSwitch';
import Input from '~/app/modules/form/Search/Input';
import { useStoreActions, useStoreState } from '~/store';
import { Material, MiscMaterial, SaveMaterialResponse } from '~/types';
import Quantity from '~/app/common/Quantity';
import classes from './style.module.scss';
import { PartLinkTypes, PartTypes } from '~/app/data/statuses/issue';
import { getMaterialStoreKey, toSaveMaterialPayload } from '../../helper';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import classNames from 'classnames';

export interface MaterialFormProps {
  open: boolean
  onClose: () => void
  material?: Material
  type: PartLinkTypes
  persistOnSave?: boolean
}

type FieldState<T> = {
  value: T;
  touched: boolean;
};

type MaterialFormState = {
  name: FieldState<string>;
  comment: FieldState<string>;
  pricePerUnit: FieldState<string | number>;
  quantity: FieldState<number>;
  costId: FieldState<string | null>;
};

type FieldName = keyof MaterialFormState;

const getInitialFormState = (material?: Material): MaterialFormState => ({
  name: { value: material?.name ?? '', touched: false },
  comment: { value: material?.comment ?? '', touched: false },
  pricePerUnit: { value: material?.pricePerUnit ?? '', touched: false },
  quantity: { value: material?.quantity ?? 1, touched: false },
  costId: { value: material?.costId ?? null, touched: false },
});

const isFormValid = (state: MaterialFormState, isAuto: boolean) => {
  const hasName = isAuto
    ? state.costId.value != null && state.costId.value !== ''
    : !!state.name.value.trim();
  return (
    hasName &&
    state.pricePerUnit.value !== '' &&
    Number(state.pricePerUnit.value) > 0 &&
    Number(state.quantity.value) > 0
  );
};

const MAX_PRICE = 999999999;

export type MaterialFormComponent = React.FC<MaterialFormProps>

const MaterialForm: MaterialFormComponent = ({
  open,
  onClose,
  material,
  type,
  persistOnSave = false,
}) => {
  const basePath = getMaterialStoreKey(type);
  const id = useStoreState(state => state[basePath].data.id);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const tempMaterials = useStoreState(state => state[basePath].tempMaterials ?? []);
  const miscMaterials = useStoreState(state => state[basePath].miscMaterials ?? []);
  const materialUpdating = useStoreState(state => state[basePath].materialUpdating ?? false);
  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);
  const saveMaterialsAction = useStoreActions(actions => actions[basePath].saveMaterials);
  const fetchMaterialsAction = useStoreActions(actions => actions[basePath].fetchMaterials);
  const fetchMiscMaterialsAction = useStoreActions(actions => actions[basePath].fetchMiscMaterials);

  const { checkPermission } = useAccessControl();
  const addManualPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addManualParts(deptPrefix))
    : false;

  const [isAuto, setIsAuto] = useState(() => {
    if (material) {
      return material.type === PartTypes.AUTO;
    }
    return miscMaterials.length === 0 ? false : true;
  });

  const [formState, setFormState] = useState<MaterialFormState>(getInitialFormState(material));
  const [selectedCategoryName, setSelectedCategoryName] = useState(material?.categoryName ?? '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dropdownTouched, setDropdownTouched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('parts');

  useEffect(() => {
    setFormState(getInitialFormState(material));
    setSelectedCategoryName(material?.categoryName ?? '');
    setSearchText('');
    setDropdownOpen(false);
    setDropdownTouched(false);

    if (material) {
      setIsAuto(material.type === PartTypes.AUTO);
    } else if (miscMaterials.length === 0) {
      setIsAuto(false);
    } else {
      setIsAuto(true);
    }
  }, [material, open, miscMaterials.length]);

  useEffect(() => {
    if (open && id) {
      fetchMiscMaterialsAction(Number(id));
    }
  }, [open, id, fetchMiscMaterialsAction]);

  useEffect(() => {
    if (!addManualPermission && !material) {
      setIsAuto(true);
    }
  }, [addManualPermission, material]);

  useEffect(() => {
    if (miscMaterials.length === 0 && !material) {
      setIsAuto(false);
    }
  }, [miscMaterials.length, material]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const filteredMisc = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return miscMaterials.filter(m => {
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        (m.categoryName ?? '').toLowerCase().includes(q)
      );
    });
  }, [miscMaterials, searchText]);

  const selectedMiscLabel = useMemo(() => {
    if (formState.costId.value == null || formState.costId.value === '') return '';
    const found = miscMaterials.find(m => m.costId === formState.costId.value);
    const name = found?.name ?? formState.name.value;
    const category = found?.categoryName ?? selectedCategoryName;
    if (!name) return '';
    return category ? `${name} (${category})` : name;
  }, [formState.costId.value, formState.name.value, miscMaterials, selectedCategoryName]);

  const updateField = useCallback((field: FieldName, value: string | number | null) => {
    setFormState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
      },
    }));
  }, []);

  const markTouched = useCallback((field: FieldName) => {
    setFormState(prev => ({
      ...prev,
      [field]: { ...prev[field], touched: true },
    }));
  }, []);

  const onCloseDialog = useCallback(() => {
    setFormState(getInitialFormState(material));
    setSelectedCategoryName(material?.categoryName ?? '');
    setSearchText('');
    setDropdownOpen(false);
    setDropdownTouched(false);
    onClose();
  }, [material, onClose]);

  const handleToggle = useCallback(() => {
    if (material) return;
    if (miscMaterials.length === 0) return;
    if (!addManualPermission && isAuto) return;
    setIsAuto(prev => {
      const next = !prev;
      setFormState(s => ({
        ...s,
        name: { value: '', touched: false },
        costId: { value: '', touched: false },
        pricePerUnit: { value: '', touched: false },
      }));
      setSelectedCategoryName('');
      setSearchText('');
      setDropdownTouched(false);
      return next;
    });
  }, [addManualPermission, isAuto, material, miscMaterials.length]);

  const handleSelectMisc = useCallback(
    (option: MiscMaterial) => {
      updateField('costId', option.costId);
      updateField('name', option.name);
      const intPrice = Math.max(0, Math.floor(Number(option.pricePerUnit) || 0));
      updateField('pricePerUnit', intPrice.toString());
      setSelectedCategoryName(option.categoryName ?? '');
      setDropdownOpen(false);
      setSearchText('');
      setDropdownTouched(true);
      setFormState(prev => ({
        ...prev,
        costId: { value: option.costId, touched: true },
      }));
    },
    [updateField]
  );

  const handleFormSubmit = useCallback(() => {
    if (isAuto && (!formState.costId.value || formState.costId.value === '')) {
      setDropdownTouched(true);
      setFormState(prev =>
        ({
          ...prev,
          costId: { ...prev.costId, touched: true },
        })
      );
    }

    if (!isFormValid(formState, isAuto)) {
      setFormState(prev =>
        Object.fromEntries(
          Object.entries(prev).map(([key, field]) => [key, { ...field, touched: true }])
        ) as MaterialFormState
      );
      setDropdownTouched(true);
      return;
    }

    const priceNum = parseInt(formState.pricePerUnit.value as string, 10);
    const parentId = Number(id);
    const updatedMaterials = [...tempMaterials];

    const selectedMisc = isAuto
      ? miscMaterials.find(m => m.costId === formState.costId.value)
      : undefined;

    const newMaterialData: Omit<Material, 'id'> = {
      issueId: parentId,
      fromType: type,
      name: isAuto ? formState.name.value : formState.name.value.trim(),
      comment: formState.comment.value.trim() || null,
      pricePerUnit: priceNum,
      quantity: formState.quantity.value,
      type: isAuto ? PartTypes.AUTO : PartTypes.MANUAL,
      costId: isAuto ? formState.costId.value : '',
      categoryId: selectedMisc?.categoryId ?? material?.categoryId ?? null,
      categoryName: isAuto
        ? (selectedMisc?.categoryName || selectedCategoryName || null)
        : null,
    };

    if (material?.id !== undefined && material?.id !== null && String(material.id) !== '') {
      const index = updatedMaterials.findIndex(m => String(m.id) === String(material.id));
      if (index >= 0) {
        updatedMaterials[index] = { ...material, ...newMaterialData, added: material.added };
      } else {
        updatedMaterials.push({ ...material, ...newMaterialData, added: true });
      }
    } else {
      updatedMaterials.push({
        id: isAuto
          ? `Auto_${formState.costId.value}_${Math.random().toExponential(0)}`
          : `Manual_${Date.now()}_${Math.random().toExponential(0)}`,
        ...newMaterialData,
        added: true,
      });
    }

    setTempMaterialsAction(updatedMaterials);

    if (persistOnSave) {
      saveMaterialsAction(toSaveMaterialPayload(id, updatedMaterials)).then((res: SaveMaterialResponse) => {
        if (res.success) {
          fetchMaterialsAction(String(id));
        }
        onCloseDialog();
      });
      return;
    }

    onCloseDialog();
  }, [
    fetchMaterialsAction,
    formState,
    id,
    isAuto,
    material,
    persistOnSave,
    saveMaterialsAction,
    setTempMaterialsAction,
    tempMaterials,
    type,
    miscMaterials,
    selectedCategoryName,
    onCloseDialog,
  ]);

  const getError = useCallback((name: string, value: any, touched: boolean, autoMode: boolean) => {
    if (!touched) return undefined;
    if (name === 'pricePerUnit') {
      return value === '' || Number(value) <= 0 ? ['required'] : undefined;
    }
    if (name === 'name' && autoMode) {
      return formState.costId.value == null || formState.costId.value === '' ? ['required'] : undefined;
    }
    return !value?.toString().trim() ? ['required'] : undefined;
  }, [formState.costId.value]);

  const Label = ({ label, required }: { label: string, required: boolean }) => (
    <div>
      {label}
      {required && <span className={classes['required-mark']}>*</span>}
    </div>
  );

  const handleCountInputChange = useCallback((raw: string) => {
    const value = raw.trim();
    if (value === '') {
      updateField('pricePerUnit', '');
      return;
    }
    if (!/^\d+$/.test(value)) {
      return;
    }
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue > MAX_PRICE) {
      return;
    }
    updateField('pricePerUnit', value);
  }, [updateField]);

  const handlePriceBlur = useCallback(() => {
    const value = formState.pricePerUnit.value;
    if (value === '') {
      markTouched('pricePerUnit');
      return;
    }
    const intVal = parseInt(value as string, 10);
    if (Number.isNaN(intVal)) {
      updateField('pricePerUnit', '');
    } else {
      updateField('pricePerUnit', intVal.toString());
    }
    markTouched('pricePerUnit');
  }, [markTouched, updateField, formState.pricePerUnit.value]);

  const isEditMode = material !== undefined;
  const canToggle =
    !isEditMode &&
    miscMaterials.length > 0 &&
    (addManualPermission || !isAuto);

  const nameDisabled = isEditMode && isAuto;

  const showNoAutoInfo = !isEditMode && miscMaterials.length === 0;

  return (
    <Modal className={classes['materialForm']} open={open} onClose={onCloseDialog} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <Icon name='arrow_back' onClick={onCloseDialog} />
          <span>{isEditMode ? t('edit') : t('add')} {t('material')}</span>
        </div>
        <div className={classes['partForm']}>
          {showNoAutoInfo && (
            <div className={classes['infoMessage']}>
              <Icon name="info" className={classes['infoIcon']} size={20} />
              <span>{t('noMaterialsAvailable')}</span>
            </div>
          )}
          <div className={classes['toggleRow']}>
            <span className={classNames(classes['toggleLabel'], { [classes['active']]: isAuto })}>
              {t('auto')}
            </span>
            <ToggleSwitch
              enabled={!isAuto}
              onClick={canToggle ? handleToggle : undefined}
              className={classNames(classes['toggle'], { [classes['toggleDisabled']]: !canToggle })}
            />
            <span className={classNames(classes['toggleLabel'], { [classes['active']]: !isAuto })}>
              {t('manual')}
            </span>
          </div>

          {isAuto ? (
            <Field label={<Label label={t('materialName')} required={true} />}>
              <div className={classes['dropdown']} ref={dropdownRef}>
                <button
                  type="button"
                  className={classNames(classes['dropdownTrigger'], {
                    [classes['disabled']]: nameDisabled,
                    [classes['fieldError']]: dropdownTouched && (!formState.costId.value || formState.costId.value === ''),
                  })}
                  disabled={nameDisabled}
                  onClick={() => {
                    if (!nameDisabled) setDropdownOpen(prev => !prev);
                  }}
                >
                  <span className={classes['dropdownValue']}>
                    {selectedMiscLabel || t('searchMaterial')}
                  </span>
                  <Icon name="arrow_drop_down" size={24} />
                </button>
                {dropdownOpen && !nameDisabled && (
                  <div className={classes['dropdownMenu']}>
                    <Input
                      autoFocus
                      placeholder={t('searchMaterial')}
                      className={classes['searchInput']}
                      inputClassName={classes['input']}
                      onSearch={setSearchText}
                      fetching={materialUpdating}
                      value={searchText}
                      iconPosition='left'
                    />
                    <ul className={classes['optionsList']}>
                      {filteredMisc.length === 0 ? (
                        <li className={classes['emptyOption']}>{t('noMaterialsFound')}</li>
                      ) : (
                        filteredMisc.map(option => (
                          <li
                            key={option.id}
                            className={classes['option']}
                            onClick={() => handleSelectMisc(option)}
                          >
                            {option.categoryName
                              ? `${option.name} (${option.categoryName})`
                              : option.name}
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </Field>
          ) : (
            <Text
              name="name"
              label={<Label label={t('materialName')} required={true} />}
              placeholder={t('materialName')}
              type="text"
              maxLength={50}
              value={formState.name.value}
              required
              errors={getError('name', formState.name.value, formState.name.touched, false)}
              onChange={e => updateField('name', e.target.value)}
              onBlur={() => markTouched('name')}
            />
          )}

          <Textarea
            name="comment"
            label={<Label label={t('comment')} required={false} />}
            placeholder={t('commentPlaceholder')}
            value={formState.comment.value}
            onChange={e => updateField('comment', e.target.value)}
            onBlur={() => markTouched('comment')}
          />

          <Text
            name="pricePerUnit"
            min={0}
            max={999999999}
            label={<Label label={t('pricePerUnit')} required={true} />}
            placeholder={t('pricePerUnit')}
            type="number"
            inputMode="numeric"
            step={1}
            value={formState.pricePerUnit.value.toString()}
            required
            errors={getError('pricePerUnit', formState.pricePerUnit.value, formState.pricePerUnit.touched, isAuto)}
            onChange={(e) => handleCountInputChange(e.target.value)}
            onBlur={handlePriceBlur}
          />

          <Field label={<Label label={t('quantity')} required={true} />}>
            <Quantity quantity={formState.quantity.value} quantityChange={q => updateField('quantity', q)} />
          </Field>

          <Button className={classes['addButton']} onClick={handleFormSubmit}>{t('save')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default MaterialForm;
